import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/app/common/components/Card'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import { useUpdatePreviewSettings } from '../hooks/useUpdatePreviewSettings'
import AdminAuthGuard from '../../../admin-auth-guard'

// Define zod schema
const colorSchema = z.object({
  color: z.string().regex(/^#[0-9A-F]{6}$/i, {
    message: 'Please enter a valid hex color',
  }),
})

// Type inference from zod schema
type ColorFormValues = z.infer<typeof colorSchema>

interface ColorSelectorProps {
  initialColor: string

  providerId: string
}

const ColorSelector: React.FC<ColorSelectorProps> = ({
  initialColor = '#000000',
  providerId,
}) => {
  // Initialize React Hook Form with zod resolver
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ColorFormValues>({
    resolver: zodResolver(colorSchema),
    defaultValues: {
      color: initialColor,
    },
  })

  const programId = useSelector(selectSelectedProgram).id

  // Get current color value from the form
  const currentColor = watch('color')

  const mutation = useUpdatePreviewSettings({
    providerId: providerId,
  })

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('color', event.target.value, { shouldValidate: true })
  }

  const onSubmit = (data: ColorFormValues) => {
    mutation.mutate({
      header_background_color: data.color,
      program_id: programId,
    })
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="font-semibold text-lg text-gray-900">Store Color</h3>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex items-center gap-3">
            <input
              type="color"
              {...register('color')}
              onChange={handleColorChange}
              className="h-10 w-14 cursor-pointer rounded-md border border-gray-300 bg-white p-1"
            />
            <span className="text-sm text-gray-600 uppercase">
              {currentColor}
            </span>
          </div>
          {errors.color && (
            <p className="mt-1 text-sm text-red-500">{errors.color.message}</p>
          )}
        </form>
      </CardContent>
      <CardFooter className="flex justify-end">
        <AdminAuthGuard requiredPermissions={['provider_management_edit']}>
          <div>
            <SubmitButton
              status={mutation.status}
              buttonTxt="Save Color"
              onClick={handleSubmit(onSubmit)}
            />
          </div>
        </AdminAuthGuard>
      </CardFooter>
    </Card>
  )
}

export default ColorSelector
